import { ActionPermissionsService } from './../../core/services/action-permissions.service';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { ActionsEnum } from 'src/app/core/data-sources/action-data-source.service';
import { UserRoles } from 'src/models/User';
import { ActivatedRoute } from '@angular/router';
import { PickingOrderType } from 'src/app/pages/picking/picking-order-type.enum';
import { CrateStateEnum } from 'src/models/Crate';
import { V3ProductsDataSourceService } from 'src/app/core/data-sources/v3/products/v3-products-data-source.service';
import { Subject } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { OutOrderStateEnum } from 'src/models/OutOrder';


interface Modal {
  orderType: PickingOrderType,
  outOrder: unknown;
}

@Component({
  selector: 'app-packing-out-order-details',
  templateUrl: './packing-out-order-details.component.html',
  styleUrls: ['./packing-out-order-details.component.scss']
})
export class PackingOutOrderDetailsComponent implements OnInit, OnDestroy {
  stateEnum = OutOrderStateEnum;
  userRole: UserRoles = null;
  actionEnum = ActionsEnum;
  progressValue = 0;
  orderData;
  orderTypeEnum = PickingOrderType;
  checkingAvailability = false;
  productAvailability: Map<string, boolean> = new Map();

  private loadTrigger$ = new Subject<void>();

  constructor(public dialogRef: MatDialogRef<PackingOutOrderDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Modal,
    private route: ActivatedRoute,
    private actionPermissionsService: ActionPermissionsService,
    private dialog: MatDialog,
    private v3ProductsService: V3ProductsDataSourceService) { }

  close(): void {
    this.dialogRef.close();
  }

  ngOnInit(): void {
    this.orderData = this.data.outOrder;
    this.setProgressValue();
    this.userRole = this.route.firstChild.snapshot.data.role;

    this.loadTrigger$.pipe(
      delay(500),
      take(1)
    ).subscribe(() => {
      this.loadAvailabilityForProducts();
    });

    this.loadTrigger$.next();
  }

  ngOnDestroy(): void {
    this.loadTrigger$.complete();
  }

  timestampToDate(timestamp) {
    return moment(timestamp).format('DD/MM/YYYY - HH:mm');
  }

  removeOrder() {
    const confirmModalDialogRef = this.dialog.open(ConfirmModalComponent, {
      panelClass: 'confirm-modal',
      autoFocus: false,
      data: {
        label: `Você tem certeza que deseja remover a ordem ${this.orderData?.number}?`,
        confirmBtnClass: 'alert'
      }
    });

    confirmModalDialogRef.afterClosed().subscribe(async (res) => {
      if (res === "SUBMITTED") {
        this.dialogRef.close({ removeOrder: true, orderId: this.orderData.id });
      }
    });
  }

  resetOrder() {
    const confirmModalDialogRef = this.dialog.open(ConfirmModalComponent, {
      panelClass: 'confirm-modal',
      autoFocus: false,
      data: {
        label: `Você tem certeza que deseja retroceder a ordem ${this.orderData?.number}?`,
        confirmBtnClass: 'success'
      }
    });

    confirmModalDialogRef.afterClosed().subscribe(async (res) => {
      if (res === "SUBMITTED") {
        this.dialogRef.close({ resetOrder: true, orderId: this.orderData.id });
      }
    });
  }

  canPerformAction(action: ActionsEnum) {
    return this.actionPermissionsService.hasPermission(action, this.userRole);
  }

  translateProductStatus(product) {
    if (product.collected === product.quantity) {
      return 'Concluído';
    } else {
      return 'Em separação';
    }
  }

  setProgressValue() {
    if (this.data.orderType !== PickingOrderType.IN_SEPARATION) return;
    this.progressValue = (this.orderData.progress.current / this.orderData.progress.required) * 100;
  }

  async loadAvailabilityForProducts(): Promise<void> {
    if (!this.orderData?.products || this.orderData.products.length === 0) {
      return;
    }

    this.checkingAvailability = true;

    try {
      const skus = this.orderData.products.map(product => product.sku).filter(sku => sku);

      if (skus.length === 0) {
        return;
      }

      const palletStates = [CrateStateEnum.STORED];
      const params = { skus, palletStates };
      const groundPositionType = ['STOCK', 'PICKING', 'PICKING_MISCELLANEOUS'];
      const products = await this.v3ProductsService.getV3ProductsCount(groundPositionType, params) || [];

      for (const itemProduct of this.orderData.products) {
        const foundProduct = products.find(p => p.sku === itemProduct.sku);
        const availableQty = foundProduct ? (foundProduct.quantity || 0) : 0;
        const isAvailable = availableQty >= itemProduct.quantity;
        this.productAvailability.set(itemProduct.sku, isAvailable);
      }
    } catch (error) {
      console.error('Error loading product availability:', error);
    } finally {
      this.checkingAvailability = false;
    }
  }

  isProductAvailable(sku: string): boolean | null {
    if (this.checkingAvailability) {
      return null;
    }
    return this.productAvailability.has(sku) ? this.productAvailability.get(sku) : null;
  }

}
